import React, { useCallback, useEffect, useState } from "react";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import {
	Button,
	Card,
	Checkbox,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Modal,
	Popconfirm,
	Select,
	Space,
	Table,
	Tag,
	TimePicker,
	Typography,
	Upload,
	message,
} from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { ConfigProvider } from "antd";
import dayjs from "dayjs";
import { antdKoKRSunday as locale } from "../../utilities/weekLocale";
import useIsMobile from "../../hooks/useIsMobile";
import PageHeader from "../common/PageHeader";
import UserWeekdayTerminalAllowances from "./UserWeekdayTerminalAllowances";

const { Title, Text } = Typography;

const AttendanceSettings = () => {
	const isMobile = useIsMobile();
	const [terminals, setTerminals] = useState([]);
	const [workTypes, setWorkTypes] = useState([]);
	const [weeklyDefaults, setWeeklyDefaults] = useState([]);
	const [securityWeeks, setSecurityWeeks] = useState([]);
	const [loading, setLoading] = useState(false);
	const [terminalModalOpen, setTerminalModalOpen] = useState(false);
	const [editingTerminal, setEditingTerminal] = useState(null);
	const [terminalForm] = Form.useForm();
	const [typeForm] = Form.useForm();
	const [securityForm] = Form.useForm();
	const [securityUploading, setSecurityUploading] = useState(false);

	const loadAll = useCallback(async () => {
		try {
			setLoading(true);
			const [termRes, typeRes, weeklyRes, secRes] =
				await Promise.all([
					useAxios.get("/attendance/terminals/"),
					useAxios.get(
						"/attendance/workday-types/",
					),
					useAxios.get(
						"/attendance/weekly-defaults/",
					),
					useAxios.get(
						"/attendance/security-shifts/",
					),
				]);
			const editableTypes = (typeRes.data || []).filter(
				(type) => type.code !== "other",
			);
			setTerminals(termRes.data || []);
			setWorkTypes(editableTypes);
			setWeeklyDefaults(
				[...(weeklyRes.data || [])].sort((a, b) => {
					const rank = (weekday) =>
						weekday === 6 ? 0 : weekday + 1;
					return (
						rank(a.weekday) -
						rank(b.weekday)
					);
				}),
			);
			setSecurityWeeks(secRes.data || []);
			typeForm.setFieldsValue({
				types: editableTypes.map((type) => ({
					id: type.id,
					name: type.name,
					start_time: type.start_time
						? dayjs(
								type.start_time,
								"HH:mm:ss",
							)
						: null,
					allowed_terminal_ids:
						type.allowed_terminal_ids || [],
				})),
			});
		} catch {
			message.error("설정 정보를 불러오지 못했습니다.");
		} finally {
			setLoading(false);
		}
	}, [typeForm]);

	useEffect(() => {
		loadAll();
	}, [loadAll]);

	const openCreateTerminal = () => {
		setEditingTerminal(null);
		terminalForm.resetFields();
		terminalForm.setFieldsValue({
			is_dawn_worship: false,
			is_security: false,
		});
		setTerminalModalOpen(true);
	};

	const openEditTerminal = (record) => {
		setEditingTerminal(record);
		terminalForm.setFieldsValue({
			terminal_id: record.terminal_id,
			name: record.name,
			is_dawn_worship: Boolean(record.is_dawn_worship),
			is_security: Boolean(record.is_security),
		});
		setTerminalModalOpen(true);
	};

	const saveTerminal = async () => {
		try {
			const values = await terminalForm.validateFields();
			const payload = {
				...values,
				is_dawn_worship: Boolean(
					values.is_dawn_worship,
				),
				is_security: Boolean(values.is_security),
			};
			if (editingTerminal) {
				await useAxios.put(
					`/attendance/terminals/${editingTerminal.id}/`,
					payload,
				);
				message.success("단말기가 수정되었습니다.");
			} else {
				await useAxios.post(
					"/attendance/terminals/",
					payload,
				);
				message.success("단말기가 추가되었습니다.");
			}
			setTerminalModalOpen(false);
			loadAll();
		} catch (err) {
			if (err?.errorFields) return;
			message.error("단말기 저장에 실패했습니다.");
		}
	};

	const deleteTerminal = async (record) => {
		try {
			await useAxios.delete(
				`/attendance/terminals/${record.id}/`,
			);
			message.success("단말기를 삭제했습니다.");
			loadAll();
		} catch {
			message.error("단말기 삭제에 실패했습니다.");
		}
	};

	const saveWorkTypes = async () => {
		try {
			const values = await typeForm.validateFields();
			await Promise.all(
				(values.types || []).map((row) =>
					useAxios.put(
						`/attendance/workday-types/${row.id}/`,
						{
							name: row.name,
							start_time: row.start_time
								? row.start_time.format(
										"HH:mm:ss",
									)
								: undefined,
							allowed_terminal_ids:
								row.allowed_terminal_ids ||
								[],
						},
					),
				),
			);
			message.success("근무 유형이 저장되었습니다.");
			loadAll();
		} catch (err) {
			if (err?.errorFields) return;
			message.error("근무 유형 저장에 실패했습니다.");
		}
	};

	const saveWeeklyDefault = async (record, workTypeId) => {
		try {
			await useAxios.put(
				`/attendance/weekly-defaults/${record.id}/`,
				{
					work_type: workTypeId || null,
				},
			);
			message.success("요일 기본값이 저장되었습니다.");
			loadAll();
		} catch {
			message.error("요일 기본값 저장에 실패했습니다.");
		}
	};

	const uploadSecurityShift = async () => {
		try {
			const values = await securityForm.validateFields();
			const file = values.file?.[0]?.originFileObj;
			if (!file) {
				message.error("엑셀 파일을 선택하세요.");
				return;
			}
			const weekStart = values.week_start.startOf("day");
			if (weekStart.day() !== 0) {
				message.error(
					"시작일은 주일(일요일)이어야 합니다.",
				);
				return;
			}
			const formData = new FormData();
			formData.append("file", file);
			formData.append(
				"week_start",
				weekStart.format("YYYY-MM-DD"),
			);
			setSecurityUploading(true);
			const res = await useAxios.post(
				"/attendance/security-shifts/",
				formData,
				{
					headers: {
						"Content-Type":
							"multipart/form-data",
					},
				},
			);
			const errors = res.data?.errors || [];
			message.success(
				`경비실 근무표 ${res.data?.created || 0}건이 반영되었습니다. (${res.data?.week_start} ~ ${res.data?.week_end})`,
			);
			if (errors.length) {
				message.warning(
					`일부 행 오류 ${errors.length}건: ${errors
						.slice(0, 3)
						.map(
							(e) =>
								`${e.row}행 ${e.error}`,
						)
						.join(" / ")}`,
				);
			}
			securityForm.resetFields();
			loadAll();
		} catch (err) {
			if (err?.errorFields) return;
			message.error(
				err?.response?.data?.error ||
					"경비실 근무표 업로드에 실패했습니다.",
			);
		} finally {
			setSecurityUploading(false);
		}
	};

	const terminalColumns = [
		{ title: "단말기ID", dataIndex: "terminal_id", width: 120 },
		{ title: "단말기명", dataIndex: "name" },
		{
			title: "용도",
			key: "flags",
			width: 200,
			render: (_, record) => (
				<Space size={4} wrap>
					{record.is_dawn_worship ? (
						<Tag color="purple">
							새벽예배
						</Tag>
					) : null}
					{record.is_security ? (
						<Tag color="blue">경비실</Tag>
					) : null}
				</Space>
			),
		},
		{
			title: "작업",
			key: "actions",
			width: 180,
			render: (_, record) => (
				<Space>
					<Button
						type="link"
						onClick={() =>
							openEditTerminal(record)
						}>
						수정
					</Button>
					<Popconfirm
						title="이 단말기를 삭제할까요?"
						okText="삭제"
						cancelText="취소"
						onConfirm={() =>
							deleteTerminal(record)
						}>
						<Button type="link" danger>
							삭제
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	const weeklyColumns = [
		{ title: "요일", dataIndex: "weekday_label", width: 80 },
		{
			title: "기본 근무 유형",
			key: "work_type",
			render: (_, record) => (
				<Select
					allowClear
					placeholder="없음 (일요일 등)"
					style={{
						minWidth: isMobile ? 0 : 220,
						width: isMobile
							? "100%"
							: undefined,
					}}
					value={record.work_type || undefined}
					options={workTypes.map((type) => ({
						value: type.id,
						label: type.name,
					}))}
					onChange={(value) =>
						saveWeeklyDefault(record, value)
					}
				/>
			),
		},
	];

	const securityWeekColumns = [
		{ title: "시작(주일)", dataIndex: "week_start", width: 140 },
		{ title: "종료(토)", dataIndex: "week_end", width: 140 },
		{
			title: "갱신",
			dataIndex: "updated_at",
			render: (v) =>
				v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-",
		},
	];

	const weekStart = Form.useWatch("week_start", securityForm);
	const weekEndLabel =
		weekStart && weekStart.day() === 0
			? weekStart.add(6, "day").format("YYYY-MM-DD (토)")
			: "-";

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="출근 설정" />
			<div className="page">
				<PageHeader title="출근 설정" />
				<Space
					direction="vertical"
					size="large"
					style={{ width: "100%" }}>
					<Card
						loading={loading}
						title={
							<Title level={4}>
								단말기
							</Title>
						}
						extra={
							<Button
								type="primary"
								icon={
									<PlusOutlined />
								}
								onClick={
									openCreateTerminal
								}>
								단말기 추가
							</Button>
						}>
						<Table
							rowKey="id"
							columns={
								terminalColumns
							}
							dataSource={terminals}
							pagination={false}
							sticky={{
								offsetHeader: 64,
							}}
						/>
					</Card>

					<Card
						loading={loading}
						title={
							<Title level={4}>
								근무 유형
							</Title>
						}
						extra={
							<Button
								type="primary"
								onClick={
									saveWorkTypes
								}>
								유형 저장
							</Button>
						}>
						<Form
							form={typeForm}
							layout="vertical">
							<Form.List name="types">
								{(fields) =>
									fields.map(
										(
											field,
										) => (
											<Space
												key={
													field.key
												}
												align="start"
												wrap
												style={{
													display: "flex",
													marginBottom: 12,
												}}>
												<Form.Item
													name={[
														field.name,
														"id",
													]}
													hidden>
													<Input />
												</Form.Item>
												<Form.Item
													label="이름"
													name={[
														field.name,
														"name",
													]}
													rules={[
														{
															required: true,
															message: "이름",
														},
													]}>
													<Input
														style={{
															width: 140,
														}}
													/>
												</Form.Item>
												<Form.Item
													label="출근 시각"
													name={[
														field.name,
														"start_time",
													]}
													rules={[
														{
															required: true,
															message: "시각",
														},
													]}>
													<TimePicker
														inputReadOnly={
															isMobile
														}
														format="HH:mm"
													/>
												</Form.Item>
												<Form.Item
													label="허용 단말기"
													name={[
														field.name,
														"allowed_terminal_ids",
													]}>
													<Select
														mode="multiple"
														style={{
															minWidth: isMobile
																? 0
																: 260,
															width: isMobile
																? "100%"
																: undefined,
														}}
														placeholder="단말기 선택"
														options={terminals.map(
															(
																item,
															) => ({
																value: item.id,
																label: `${item.name} (${item.terminal_id})`,
															}),
														)}
													/>
												</Form.Item>
											</Space>
										),
									)
								}
							</Form.List>
						</Form>
					</Card>

					<Card
						loading={loading}
						title={
							<Title level={4}>
								요일별 기본값
							</Title>
						}>
						<Table
							rowKey="id"
							columns={weeklyColumns}
							dataSource={
								weeklyDefaults
							}
							pagination={false}
							sticky={{
								offsetHeader: 64,
							}}
						/>
					</Card>

					<UserWeekdayTerminalAllowances
						terminals={terminals}
						loading={loading}
					/>

					<Card
						loading={loading}
						title={
							<Title level={4}>
								경비실 주간
								근무표
							</Title>
						}>
						<Text
							type="secondary"
							style={{
								display: "block",
								marginBottom: 12,
							}}>
							엑셀 열: 근무자,
							이번주일, 월, 화, 수,
							목, 금, 토 · 시작일은
							주일만 선택 · 같은 주를
							다시 올리면 덮어씁니다.
						</Text>
						<Form
							form={securityForm}
							layout="inline"
							style={{
								marginBottom: 16,
							}}>
							<Form.Item
								name="week_start"
								label="시작일(주일)"
								rules={[
									{
										required: true,
										message: "시작일을 선택하세요",
									},
								]}>
								<DatePicker
									inputReadOnly={
										isMobile
									}
									format="YYYY-MM-DD"
									disabledDate={(
										current,
									) =>
										current &&
										current.day() !==
											0
									}
								/>
							</Form.Item>
							<Form.Item label="종료일">
								<Text>
									{
										weekEndLabel
									}
								</Text>
							</Form.Item>
							<Form.Item
								name="file"
								label="엑셀"
								valuePropName="fileList"
								getValueFromEvent={(
									e,
								) =>
									Array.isArray(
										e,
									)
										? e
										: e?.fileList
								}
								rules={[
									{
										required: true,
										message: "파일을 선택하세요",
									},
								]}>
								<Upload
									beforeUpload={() =>
										false
									}
									maxCount={
										1
									}
									accept=".xlsx,.xls">
									<Button
										icon={
											<UploadOutlined />
										}>
										파일
										선택
									</Button>
								</Upload>
							</Form.Item>
							<Form.Item>
								<Button
									type="primary"
									loading={
										securityUploading
									}
									onClick={
										uploadSecurityShift
									}>
									업로드
								</Button>
							</Form.Item>
						</Form>
						<Table
							rowKey="id"
							columns={
								securityWeekColumns
							}
							dataSource={
								securityWeeks
							}
							pagination={false}
							size="small"
							sticky={{
								offsetHeader: 64,
							}}
						/>
					</Card>
				</Space>

				<Modal
					title={
						editingTerminal
							? "단말기 수정"
							: "단말기 추가"
					}
					open={terminalModalOpen}
					onOk={saveTerminal}
					onCancel={() =>
						setTerminalModalOpen(false)
					}
					okText="저장"
					cancelText="취소"
					destroyOnClose>
					<Form
						form={terminalForm}
						layout="vertical">
						<Form.Item
							name="terminal_id"
							label="단말기ID"
							rules={[
								{
									required: true,
									message: "단말기ID를 입력하세요",
								},
							]}>
							<InputNumber
								style={{
									width: "100%",
								}}
							/>
						</Form.Item>
						<Form.Item
							name="name"
							label="단말기명"
							rules={[
								{
									required: true,
									message: "단말기명을 입력하세요",
								},
							]}>
							<Input />
						</Form.Item>
						<Form.Item
							name="is_dawn_worship"
							valuePropName="checked">
							<Checkbox>
								새벽예배용
								단말기
							</Checkbox>
						</Form.Item>
						<Form.Item
							name="is_security"
							valuePropName="checked">
							<Checkbox>
								경비실용 단말기
							</Checkbox>
						</Form.Item>
					</Form>
				</Modal>
			</div>
		</ConfigProvider>
	);
};

export default AttendanceSettings;
